import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TimeField } from '../components/ui/time-field';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Clock, DollarSign, Eye, Zap, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const OT_METHODS = [
  { value: 'daily_threshold', label: 'Daily Threshold', desc: 'OT after X hours per day' },
  { value: 'weekly_threshold', label: 'Weekly Threshold', desc: 'OT after X hours per week (e.g. 38h)' },
  { value: 'day_of_week', label: 'Day of Week', desc: 'Different rates for Sat/Sun/Public Holidays' },
  { value: 'outside_hours', label: 'Outside Normal Hours', desc: 'OT before/after normal window' },
];

const CALC_METHODS = [
  { value: 'fixed', label: 'Fixed amount' },
  { value: 'per_hour', label: 'Per hour' },
  { value: 'per_day', label: 'Per day' },
  { value: 'per_shift', label: 'Per shift' },
];

const SCOPES = [
  { value: 'global', label: 'Global (all workers)' },
  { value: 'worker_type', label: 'By Worker Type' },
  { value: 'site', label: 'By Site' },
  { value: 'customer', label: 'By Customer' },
  { value: 'equipment_type', label: 'By Equipment Type' },
];

const DAY_RATES = [
  { value: 'ordinary', label: 'Ordinary' },
  { value: 'ot_1_5', label: 'OT 1.5x' },
  { value: 'ot_2_0', label: 'OT 2.0x' },
];

const emptyOTConfig = {
  method: 'daily_threshold',
  ordinary_hours: 8,
  ot_1_5_hours: 10,
  weekly_ordinary: 38,
  saturday_rate: 'ot_1_5',
  sunday_rate: 'ot_2_0',
  public_holiday_rate: 'ot_2_0',
  normal_start: '06:00',
  normal_end: '18:00',
};

const emptyAllowConfig = {
  allowance_type_id: '',
  allowance_type_name: '',
  calc_method: 'fixed',
  amount: 0,
  min_hours: null,
  max_amount_per_day: null,
  conditions: [],
};

const emptyForm = {
  name: '',
  description: '',
  rule_type: 'overtime',
  is_active: true,
  priority: 0,
  scope: 'global',
  worker_type_ids: [],
  site_ids: [],
  customer_ids: [],
  equipment_type_ids: [],
  overtime_config: { ...emptyOTConfig },
  allowance_config: { ...emptyAllowConfig },
};

export default function PayrollRulesManager({ api }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Lookups for scope selectors
  const [workerTypes, setWorkerTypes] = useState([]);
  const [allowanceTypes, setAllowanceTypes] = useState([]);

  const fetchRules = useCallback(async () => {
    try {
      const res = await api.get('/payroll-rules');
      setRules(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [api]);

  const fetchLookups = useCallback(async () => {
    try {
      const [wt, at] = await Promise.all([
        api.get('/worker-types/all').catch(() => ({ data: [] })),
        api.get('/allowance-types').catch(() => ({ data: [] })),
      ]);
      setWorkerTypes(wt.data || []);
      setAllowanceTypes(at.data || []);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchRules(); fetchLookups(); }, [fetchRules, fetchLookups]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Rule name is required'); return; }

    const payload = {
      name: form.name,
      description: form.description,
      rule_type: form.rule_type,
      is_active: form.is_active,
      priority: form.priority,
      scope: form.scope,
      worker_type_ids: form.scope === 'worker_type' ? form.worker_type_ids : [],
      site_ids: form.scope === 'site' ? form.site_ids : [],
      customer_ids: form.scope === 'customer' ? form.customer_ids : [],
      equipment_type_ids: form.scope === 'equipment_type' ? form.equipment_type_ids : [],
      overtime_config: form.rule_type === 'overtime' ? form.overtime_config : null,
      allowance_config: form.rule_type === 'allowance' ? {
        ...form.allowance_config,
        allowance_type_name: allowanceTypes.find(a => a.id === form.allowance_config.allowance_type_id)?.name || '',
      } : null,
    };

    try {
      if (editing) {
        await api.put(`/payroll-rules/${editing.id}`, payload);
        toast.success('Rule updated');
      } else {
        await api.post('/payroll-rules', payload);
        toast.success('Rule created');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      description: r.description || '',
      rule_type: r.rule_type,
      is_active: r.is_active,
      priority: r.priority || 0,
      scope: r.scope || 'global',
      worker_type_ids: r.worker_type_ids || [],
      site_ids: r.site_ids || [],
      customer_ids: r.customer_ids || [],
      equipment_type_ids: r.equipment_type_ids || [],
      overtime_config: r.overtime_config ? { ...emptyOTConfig, ...r.overtime_config } : { ...emptyOTConfig },
      allowance_config: r.allowance_config ? { ...emptyAllowConfig, ...r.allowance_config } : { ...emptyAllowConfig },
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/payroll-rules/${deleteTarget.id}`);
      toast.success('Rule deleted');
      setDeleteTarget(null);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggle = async (r) => {
    try {
      await api.put(`/payroll-rules/${r.id}`, { is_active: !r.is_active });
      fetchRules();
    } catch { toast.error('Failed to toggle rule'); }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    const end = new Date().toISOString().slice(0, 10);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 14);
    const start = startDate.toISOString().slice(0, 10);
    try {
      const res = await api.post('/payroll-rules/preview', { period_start: start, period_end: end });
      setPreviewData(res.data);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setPreviewLoading(false); }
  };

  const methodLabel = (r) => {
    if (r.rule_type === 'overtime') {
      const m = r.overtime_config?.method || 'daily_threshold';
      return OT_METHODS.find(o => o.value === m)?.label || m;
    }
    const c = r.allowance_config?.calc_method || 'fixed';
    return CALC_METHODS.find(o => o.value === c)?.label || c;
  };

  const scopeLabel = (s) => SCOPES.find(o => o.value === s)?.label || s;

  const otRules = rules.filter(r => r.rule_type === 'overtime');
  const allowRules = rules.filter(r => r.rule_type === 'allowance');

  if (loading) return <div className="text-muted-foreground py-8 text-center">Loading payroll rules...</div>;

  return (
    <div className="space-y-6" data-testid="payroll-rules-manager">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Payroll Rules</h3>
          <p className="text-sm text-muted-foreground">
            Configure overtime calculations and automatic allowances for payroll
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={previewLoading || rules.length === 0}
            data-testid="preview-rules-btn"
          >
            <Eye className="h-4 w-4 mr-2" />
            {previewLoading ? 'Loading...' : 'Preview'}
          </Button>
          <Button
            onClick={() => { setEditing(null); setForm({ ...emptyForm }); setDialogOpen(true); }}
            className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
            data-testid="add-payroll-rule-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Rule
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overtime" className="w-full">
        <TabsList>
          <TabsTrigger value="overtime" data-testid="rules-tab-overtime">
            <Clock className="h-4 w-4 mr-1.5" /> Overtime ({otRules.length})
          </TabsTrigger>
          <TabsTrigger value="allowance" data-testid="rules-tab-allowance">
            <DollarSign className="h-4 w-4 mr-1.5" /> Allowances ({allowRules.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overtime" className="mt-4">
          <RuleTable
            rules={otRules}
            onEdit={handleEdit}
            onDelete={setDeleteTarget}
            onToggle={handleToggle}
            methodLabel={methodLabel}
            scopeLabel={scopeLabel}
            emptyMsg="No overtime rules configured. Add one to customise OT thresholds."
          />
        </TabsContent>

        <TabsContent value="allowance" className="mt-4">
          <RuleTable
            rules={allowRules}
            onEdit={handleEdit}
            onDelete={setDeleteTarget}
            onToggle={handleToggle}
            methodLabel={methodLabel}
            scopeLabel={scopeLabel}
            emptyMsg="No allowance rules configured. Add one to auto-calculate allowances."
            showAmount
          />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Add'} Payroll Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Name + Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Rule Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Standard Daily OT"
                  data-testid="rule-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Rule Type</Label>
                <Select
                  value={form.rule_type}
                  onValueChange={v => setForm({ ...form, rule_type: v })}
                >
                  <SelectTrigger data-testid="rule-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="overtime">Overtime</SelectItem>
                    <SelectItem value="allowance">Allowance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of when this rule applies"
              />
            </div>

            {/* Scope + Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Scope</Label>
                <Select value={form.scope} onValueChange={v => setForm({ ...form, scope: v })}>
                  <SelectTrigger data-testid="rule-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priority (higher wins)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.priority}
                  onChange={e => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                  data-testid="rule-priority-input"
                />
              </div>
            </div>

            {/* Scope-specific selectors */}
            {form.scope === 'worker_type' && (
              <div className="space-y-1.5">
                <Label>Worker Types</Label>
                <div className="flex flex-wrap gap-2 border rounded-md p-3 bg-muted/30">
                  {workerTypes.filter(wt => wt.is_active).map(wt => {
                    const checked = (form.worker_type_ids || []).includes(wt.id);
                    return (
                      <label key={wt.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const ids = checked
                              ? form.worker_type_ids.filter(i => i !== wt.id)
                              : [...(form.worker_type_ids || []), wt.id];
                            setForm({ ...form, worker_type_ids: ids });
                          }}
                          className="accent-[#ffd108]"
                        />
                        {wt.name}
                      </label>
                    );
                  })}
                  {workerTypes.filter(wt => wt.is_active).length === 0 && (
                    <span className="text-muted-foreground text-sm">No active worker types</span>
                  )}
                </div>
              </div>
            )}

            {/* OT Config */}
            {form.rule_type === 'overtime' && (
              <OvertimeConfigEditor
                config={form.overtime_config}
                onChange={c => setForm({ ...form, overtime_config: c })}
              />
            )}

            {/* Allowance Config */}
            {form.rule_type === 'allowance' && (
              <AllowanceConfigEditor
                config={form.allowance_config}
                onChange={c => setForm({ ...form, allowance_config: c })}
                allowanceTypes={allowanceTypes}
              />
            )}

            {/* Active toggle */}
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={v => setForm({ ...form, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
              data-testid="save-rule-btn"
            >
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payroll Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <PreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} data={previewData} />
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────── */

function RuleTable({ rules, onEdit, onDelete, onToggle, methodLabel, scopeLabel, emptyMsg, showAmount }) {
  if (rules.length === 0) {
    return <p className="text-muted-foreground text-center py-8">{emptyMsg}</p>;
  }
  return (
    <Card className="card-industrial">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Scope</TableHead>
              {showAmount && <TableHead>Amount</TableHead>}
              <TableHead>Priority</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.map(r => (
              <TableRow key={r.id} data-testid={`payroll-rule-row-${r.id}`}>
                <TableCell>
                  <div>
                    <span className="font-medium">{r.name}</span>
                    {r.description && <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>}
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline">{methodLabel(r)}</Badge></TableCell>
                <TableCell className="text-sm">{scopeLabel(r.scope)}</TableCell>
                {showAmount && (
                  <TableCell>${(r.allowance_config?.amount || 0).toFixed(2)}</TableCell>
                )}
                <TableCell>{r.priority}</TableCell>
                <TableCell>
                  <Switch checked={r.is_active} onCheckedChange={() => onToggle(r)} />
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(r)}>
                        <Pencil className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onDelete(r)} className="text-red-500">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function OvertimeConfigEditor({ config, onChange }) {
  const c = config || emptyOTConfig;
  const set = (k, v) => onChange({ ...c, [k]: v });
  return (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/20">
      <h4 className="font-medium flex items-center gap-2"><Clock className="h-4 w-4" /> Overtime Configuration</h4>
      <div className="space-y-1.5">
        <Label>OT Method</Label>
        <Select value={c.method} onValueChange={v => set('method', v)}>
          <SelectTrigger data-testid="ot-method-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OT_METHODS.map(m => (
              <SelectItem key={m.value} value={m.value}>
                <div>
                  <div className="font-medium">{m.label}</div>
                  <div className="text-xs text-muted-foreground">{m.desc}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {c.method === 'daily_threshold' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Ordinary hours (daily)</Label>
            <Input type="number" min={0} step={0.5} value={c.ordinary_hours} onChange={e => set('ordinary_hours', parseFloat(e.target.value) || 0)} data-testid="ot-ordinary-hours" />
          </div>
          <div className="space-y-1.5">
            <Label>OT 1.5x ceiling (daily)</Label>
            <Input type="number" min={0} step={0.5} value={c.ot_1_5_hours} onChange={e => set('ot_1_5_hours', parseFloat(e.target.value) || 0)} data-testid="ot-15-hours" />
          </div>
        </div>
      )}

      {c.method === 'weekly_threshold' && (
        <div className="space-y-1.5">
          <Label>Weekly ordinary hours</Label>
          <Input type="number" min={0} step={0.5} value={c.weekly_ordinary} onChange={e => set('weekly_ordinary', parseFloat(e.target.value) || 0)} data-testid="ot-weekly-ordinary" />
          <p className="text-xs text-muted-foreground">Hours above this weekly total become overtime</p>
        </div>
      )}

      {c.method === 'day_of_week' && (
        <div className="grid grid-cols-3 gap-4">
          {[['saturday_rate', 'Saturday'], ['sunday_rate', 'Sunday'], ['public_holiday_rate', 'Public Holiday']].map(([key, label]) => (
            <div key={key} className="space-y-1.5">
              <Label>{label}</Label>
              <Select value={c[key] || 'ordinary'} onValueChange={v => set(key, v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAY_RATES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
          <div className="col-span-3 grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Weekday ordinary hours</Label>
              <Input type="number" min={0} step={0.5} value={c.ordinary_hours} onChange={e => set('ordinary_hours', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="space-y-1.5">
              <Label>Weekday OT 1.5x ceiling</Label>
              <Input type="number" min={0} step={0.5} value={c.ot_1_5_hours} onChange={e => set('ot_1_5_hours', parseFloat(e.target.value) || 0)} />
            </div>
          </div>
        </div>
      )}

      {c.method === 'outside_hours' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Normal start time</Label>
            <TimeField value={c.normal_start} onChange={v => set('normal_start', v)} data-testid="ot-normal-start" />
          </div>
          <div className="space-y-1.5">
            <Label>Normal end time</Label>
            <TimeField value={c.normal_end} onChange={v => set('normal_end', v)} data-testid="ot-normal-end" />
          </div>
          <p className="col-span-2 text-xs text-muted-foreground">Hours outside this window are classified as overtime</p>
        </div>
      )}
    </div>
  );
}

function AllowanceConfigEditor({ config, onChange, allowanceTypes }) {
  const c = config || emptyAllowConfig;
  const set = (k, v) => onChange({ ...c, [k]: v });
  return (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/20">
      <h4 className="font-medium flex items-center gap-2"><DollarSign className="h-4 w-4" /> Allowance Configuration</h4>
      <div className="space-y-1.5">
        <Label>Allowance Type <span className="text-red-500">*</span></Label>
        <Select value={c.allowance_type_id} onValueChange={v => set('allowance_type_id', v)}>
          <SelectTrigger data-testid="allowance-type-rule-select">
            <SelectValue placeholder="Select allowance type..." />
          </SelectTrigger>
          <SelectContent>
            {allowanceTypes.filter(a => a.is_active !== false).map(a => (
              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Calculation Method</Label>
          <Select value={c.calc_method} onValueChange={v => set('calc_method', v)}>
            <SelectTrigger data-testid="allowance-calc-method-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CALC_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Amount ($)</Label>
          <Input type="number" min={0} step={0.01} value={c.amount} onChange={e => set('amount', parseFloat(e.target.value) || 0)} data-testid="allowance-amount-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Min hours (optional)</Label>
          <Input type="number" min={0} step={0.5} value={c.min_hours || ''} onChange={e => set('min_hours', e.target.value ? parseFloat(e.target.value) : null)} placeholder="No minimum" />
        </div>
        <div className="space-y-1.5">
          <Label>Max per day (optional)</Label>
          <Input type="number" min={0} step={0.01} value={c.max_amount_per_day || ''} onChange={e => set('max_amount_per_day', e.target.value ? parseFloat(e.target.value) : null)} placeholder="No cap" />
        </div>
      </div>
    </div>
  );
}

function PreviewDialog({ open, onOpenChange, data }) {
  if (!data) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-[#ffd108]" /> Rules Preview (Last 14 Days)
          </DialogTitle>
        </DialogHeader>

        {data.ot_preview?.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">Overtime Splits ({data.ot_preview.length} entries)</h4>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Worker</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Before</TableHead>
                    <TableHead>After</TableHead>
                    <TableHead>Rule</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.ot_preview.slice(0, 20).map((e, i) => {
                    const changed = e.before.ordinary !== e.after.ordinary ||
                      e.before.overtime_1_5 !== e.after.overtime_1_5 ||
                      e.before.overtime_2_0 !== e.after.overtime_2_0;
                    return (
                      <TableRow key={i} className={changed ? 'bg-[#ffd108]/5' : ''}>
                        <TableCell className="font-medium text-sm">{e.worker_name}</TableCell>
                        <TableCell className="text-sm">{e.date}</TableCell>
                        <TableCell className="text-sm">{e.total_hours}h</TableCell>
                        <TableCell className="text-xs">
                          Ord:{e.before.ordinary} / 1.5x:{e.before.overtime_1_5} / 2x:{e.before.overtime_2_0}
                        </TableCell>
                        <TableCell className="text-xs">
                          <span className={changed ? 'font-semibold text-[#ffd108]' : ''}>
                            Ord:{e.after.ordinary} / 1.5x:{e.after.overtime_1_5} / 2x:{e.after.overtime_2_0}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{e.rule_name || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {data.allowance_preview?.length > 0 && (
          <div className="space-y-3 mt-4">
            <h4 className="font-medium">Auto-Allowances ({data.allowance_preview.length} entries)</h4>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Worker</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Allowance</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Rule</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.allowance_preview.slice(0, 20).map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{e.worker_name}</TableCell>
                      <TableCell className="text-sm">{e.date}</TableCell>
                      <TableCell className="text-sm">{e.allowance_type}</TableCell>
                      <TableCell className="font-medium">${e.amount?.toFixed(2)}</TableCell>
                      <TableCell className="text-xs">{e.calc_method}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{e.rule_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {(data.ot_preview?.length === 0 && data.allowance_preview?.length === 0) && (
          <p className="text-muted-foreground py-6 text-center">
            No changes would be applied. Rules may not match any timesheets in the last 14 days.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
